import { useMemo } from 'react';
import { VenueNameDisplay } from 'src/components/Events/EventInfo';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import {
  PosMultiSelect,
  PosMultiSelectProps,
} from 'src/core/POS/PosMultiSelect';
import { ContentId } from 'src/utils/constants/contentId';
import { getDescription } from 'src/utils/eventWithDataUtils';

type VenueSelectorProps = Omit<PosMultiSelectProps, 'valueOptionsContent'> & {
  forceUseCatalogV2?: boolean;
};

export const VenueSelector = ({
  placeholderText,
  forceUseCatalogV2,
  ...props
}: VenueSelectorProps) => {
  const { data: catalogData, isLoading } = useCatalogDataContext();

  const { allVenues, allVenueDisplay, allVenueSort } = useMemo(() => {
    if (!catalogData?.events) {
      return { allVenues: {}, allVenueDisplay: {}, allVenueSort: {} };
    }

    const allVenues = {} as Record<string, string>;
    const allVenueDisplay = {} as Record<string, () => JSX.Element>;
    const allVenueSort = {} as Record<string, string>;

    Object.values(catalogData.venues).forEach((venue) => {
      if (venue && venue.viagId) {
        allVenues[venue.viagId] = venue.name;

        allVenueDisplay[venue.viagId] = () => (
          <VenueNameDisplay
            venueName={venue.name}
            venueLocation={getDescription(venue)}
          />
        );

        allVenueSort[venue.viagId] = venue.name;
      }
    });

    return { allVenues, allVenueDisplay, allVenueSort };
  }, [catalogData]);

  const hasNoOption = Object.values(allVenues).length == 0;

  return (
    <PosMultiSelect
      {...props}
      searchable
      loading={isLoading}
      placeholderText={
        hasNoOption ? ContentId.EventSelectorPlaceholder : placeholderText
      }
      useVirtualWindow
      valueOptionsContent={allVenues}
      valueOptionsDisplay={allVenueDisplay}
      valueOptionsSort={allVenueSort}
      sortMode="ascending"
    />
  );
};
